import { useLayoutEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { toast } from 'react-toastify';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { RichTextElement } from '@kontent-ai/gatsby-components';
import { isRichTextValid } from '../../../RichTextBlock/utils';
import {
  StyledFigure,
  StyledCodeSnippet,
  StyledDescription,
  StyledButton,
  StyledImage,
  StyledTooltip,
} from './CodeSnippet.styles';
import hljs from 'highlight.js/lib/core';
import hljsLanguages from '../../utils/hljs.languages';
import hljsLinenumbers from '../../utils/hljs.linenumbers';
import HLJSHighlighting from '../../utils/hljs.highlighting';

Object.entries(hljsLanguages).forEach(([k, v]) => hljs.registerLanguage(k, v));

const CodeSnippet = ({
  codeSnippetElements: elements,
  system: { id },
}: Queries.CODE_SNIPPETFragment) => {
  const codeRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    hljs.addPlugin(new HLJSHighlighting());
    hljs.addPlugin({ 'after:highlightElement': hljsLinenumbers });
    if (codeRef.current) {
      hljs.highlightElement(codeRef.current);
    }
  }, []);

  const { code_snippet, language, options, description } = elements;
  const codeAttributes: {
    className?: string;
  } = {};

  const codeClasses: string[] = [];

  const hasOption = (optionName: string) =>
    Boolean(options?.value?.find((option) => option === optionName));

  const codeSnippetValue = code_snippet?.value;
  const hasLanguage = Boolean(language?.value) && language?.value !== 'plain_text';

  if (hasLanguage) {
    codeClasses.push(`language-${language?.value}`);
  } else if (language?.value === 'plain_text') {
    codeClasses.push('language-plaintext');
  }

  if (hasOption('linenumbers')) {
    codeClasses.push('line-numbers');
  }

  if (hasOption('has_highlighting')) {
    codeClasses.push('has-highlighting');
  }

  if (codeClasses.length > 0) {
    codeAttributes.className = codeClasses.join(' ');
  }

  const [copied, setCopied] = useState(false);
  const { copy: copyToClipboard } = useCopyToClipboard();
  const ref = useRef(null);

  useOnClickOutside(ref, () => {
    setCopied(false);
  });

  const onClickCopyToClipboard = () => {
    copyToClipboard(codeSnippetValue);
    toast.success('Content was copied to clipboard!');
    setCopied(true);
  };

  return (
    <StyledFigure>
      <StyledButton onClick={onClickCopyToClipboard} ref={ref} data-tooltip-id={id}>
        <StyledTooltip id={id}>
          {copied ? 'Copied to clipboard' : 'Copy to clipboard'}
        </StyledTooltip>
        <StyledImage width="19" height="25" viewBox="0 0 19 25" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18.071 0.799988H3.71382C3.59596 0.799988 3.49953 0.896416 3.49953 1.01427V2.51427C3.49953 2.63213 3.59596 2.72856 3.71382 2.72856H16.9995V21.1571C16.9995 21.275 17.096 21.3714 17.2138 21.3714H18.7138C18.8317 21.3714 18.9281 21.275 18.9281 21.1571V1.65713C18.9281 1.18302 18.5451 0.799988 18.071 0.799988ZM14.6424 4.22856H0.928104C0.453997 4.22856 0.070961 4.61159 0.070961 5.0857V19.3009C0.070961 19.5286 0.162032 19.7455 0.322747 19.9062L4.96471 24.5482C5.02364 24.6071 5.0906 24.6553 5.16293 24.6955V24.7464H5.27543C5.36918 24.7812 5.46828 24.8 5.57007 24.8H14.6424C15.1165 24.8 15.4995 24.417 15.4995 23.9428V5.0857C15.4995 4.61159 15.1165 4.22856 14.6424 4.22856ZM5.16025 22.0196L2.854 19.7107H5.16025V22.0196ZM13.571 22.8714H6.87453V19.0678C6.87453 18.4759 6.39507 17.9964 5.8031 17.9964H1.99953V6.15713H13.571V22.8714Z"
            fill="currentColor"
          />
        </StyledImage>
      </StyledButton>
      <pre>
        <StyledCodeSnippet
          {...codeAttributes}
          hasHorizontalScrollbar={hasOption('scrollbar_x')}
          ref={codeRef}
        >
          {codeSnippetValue}
        </StyledCodeSnippet>
      </pre>
      {description?.value && isRichTextValid(description?.value) && (
        <StyledDescription>
          <RichTextElement value={description.value} />
        </StyledDescription>
      )}
    </StyledFigure>
  );
};

export default CodeSnippet;

export const fragments = graphql`
  fragment CODE_SNIPPET on kontent_item_code_snippet {
    __typename
    system {
      id
      codename
    }
    codeSnippetElements: elements {
      code_snippet {
        value
      }
      language {
        value
      }
      options {
        value
      }
      description {
        value
      }
    }
  }
`;
